import { PERMISSION_CODES } from 'shared/constants';
import {
  isEmpty,
  uniqBy
} from 'lodash';

const mapRolePermissions = (role, permissions) => {
  if (!role) {
    return {
      permissions: [],
    };
  }
  return {
    role: role.role,
    permissions: role.rolePermission.map(perm => ({
      id: perm.permissionId,
      code: permissions.find(p => p.id === perm.permissionId).code,
    })),
  };
};

const Permissions = {
  testCenters: {},
  testEvents: {},
  testRooms: {},
  set tcPermissions(testCenters) {
    !isEmpty(testCenters) && testCenters.forEach((tc) => {
      const { role } = tc;
      this.testCenters[tc.testCenterCycleId] = mapRolePermissions(role, this.permissions);
    });
  },
  get tcPermissions() {
    return this.testCenters;
  },
  set eventPermissions(events) {
    !isEmpty(events) && events.forEach((event) => {
      const { role } = event;
      if (this.testEvents[event.testCenterEventId]) {
        const permissions = uniqBy([
          ...this.testEvents[event.testCenterEventId].permissions,
          ...mapRolePermissions(role, this.permissions).permissions,
        ], 'id');
        this.testEvents[event.testCenterEventId] = {
          ...this.testEvents[event.testCenterEventId],
          permissions,
        };
        return;
      }
      this.testEvents[event.testCenterEventId] = mapRolePermissions(role, this.permissions);
    });
  },
  get eventPermissions() {
    return this.testEvents;
  },
  set roomPermissions(events) {
    !isEmpty(events) && events.forEach((event) => {
      if (event.roomId) {
        const { role } = event;
        this.testRooms[event.roomId] = mapRolePermissions(role, this.permissions);
        this.testRooms[event.roomId].eventId = event.testCenterEventId;
      }
    });
  },
  get roomPermissions() {
    return this.testRooms;
  },
  setup(user, permissions) {
    this.clean();
    this.permissions = permissions;
    this.tcPermissions = user.testCenterCyclePersonnels;
    this.eventPermissions = user.testCenterEventPersonnels;
    this.roomPermissions = user.testCenterEventPersonnels;
  },
  /**
   * @param {String} permissionType: testCenters, testEvents, testRooms
   * @param {String} resourseType 
   * @param {Number} id 
   */
  hasAccessTo(permissionType, resourseType, id) {
    if (!this[resourseType] || !this[resourseType][id]) {
      return false;
    }
    
    return this[resourseType][id].permissions
      .some(perm => perm.code === PERMISSION_CODES[permissionType]);
  },
  hasTestCenterManage(tcId) {
    if (!this.testCenters[tcId]) {
      return false;
    }
    return this.testCenters[tcId].permissions.some(perm => perm.code === PERMISSION_CODES.tcManage);
  },
  hasEventManage(eventId) {
    if (!this.testEvents[eventId]) {
      return false;
    }
    return this.testEvents[eventId].permissions
      .some(evPerm => evPerm.code === PERMISSION_CODES.eventManage);
  },
  hasRoomManage(eventId, roomId) {
    const room = this.testEvents[eventId] || this.testRooms[roomId];
    if (!room) {
      return false;
    }
    return room.permissions
      .some(evPerm => evPerm.code === PERMISSION_CODES.roomManage);
  },
  hasRoomAccess(eventId) {
    const room = this.testEvents[eventId] || this.testRooms[eventId];
    if (!room) {
      return false;
    }
    return room.permissions
      .some(evPerm => evPerm.code === PERMISSION_CODES.allRoomAccess);
  },
  hasManualTimerControl(tcId, eventId){
    if (!this.testCenters[tcId] && !this.testEvents[eventId]) {
      return false;
    }
    if (this.testCenters[tcId]) {
      return this.testCenters[tcId].permissions.some(perm => perm.code === PERMISSION_CODES.manualTimerControl);
    }

    return this.testEvents[eventId].permissions
      .some(evPerm => evPerm.code === PERMISSION_CODES.manualTimerControl);
  },
  hasPageAccess(params) {
    return Object.keys(params).every(key => checkPermission(key, params[key]));
  },
  hasTestRooms(tcId, eventId, roomId) {
    if (!this.testRooms[roomId] && (this.testEvents[eventId] || this.testCenters[tcId])) {
      return true;
    }

    if (this.testRooms[roomId] && this.testRooms[roomId].eventId === eventId) {
      return true;
    }

    return false;
  },
  clean() {
    this.testCenters = {};
    this.testEvents = {};
    this.testRooms = {};
  },
};

const checkPermission = (key, id) => {
  const permissionMethods = {
    tcId: () => Permissions.hasTestCenterManage(id),
    eventId: () => Permissions.hasEventManage(id),
    roomId: () => Permissions.hasRoomManage(id),
  };

  if (!permissionMethods[key]) {
    return true;
  }

  return permissionMethods[key](id);
};

export default Permissions;
